import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAppSelector } from '../store/hooks';
import { TAX_QUICK_QUESTIONS } from '../api/ai-tax-advisor';
import AIDataConnectionPanel from '../components/ai/AIDataConnectionPanel';

interface AICommentaryResponse {
  available?: boolean;
  upgradeRequired?: boolean;
  message?: string;
  headline?: string;
  performanceNarrative?: string;
  topMovers?: string[];
  keyRiskFlags?: string[];
  suggestedActions?: Array<{ action: string; label: string }>;
  generatedAt?: string;
  cached?: boolean;
}

interface AIQueryResponse {
  answer?: string;
  response?: string;
  queriesUsedToday?: number;
  queriesRemainingToday?: number;
}

/** Attempt to parse a "long-term in N days" countdown from the AI answer. */
function parseDaysToLongTerm(answer: string): number | null {
  const match = /(\d+)\s*(?:more\s+)?(?:days?|months?\s+\((\d+)\s*days?\))\s*(?:until|for|to)\s*(?:long[- ]term|12[- ]month)/i.exec(answer);
  if (!match) return null;
  return parseInt(match[1], 10);
}

/** Detect whether the answer contains tax-specific content worth highlighting. */
function isTaxAnswer(answer: string): boolean {
  const lower = answer.toLowerCase();
  return (
    lower.includes('short-term') ||
    lower.includes('long-term') ||
    lower.includes('tax') ||
    lower.includes('wash sale') ||
    lower.includes('cost basis') ||
    lower.includes('capital gain')
  );
}

/** Extract an estimated tax saving amount (e.g. "saving of $382") from the answer. */
function parseTaxSaving(answer: string): number | null {
  const match = /saving\s+of\s+\$?([\d,]+(?:\.\d+)?)/i.exec(answer);
  if (!match) return null;
  return parseFloat(match[1].replace(',', ''));
}

const AIInsightsPage: React.FC = () => {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const tier = useAppSelector((s) => s.auth.user?.tier);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentary, setCommentary] = useState<AICommentaryResponse | null>(null);

  const [query, setQuery] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [queryAnswer, setQueryAnswer] = useState<string | null>(null);
  const [queriesRemaining, setQueriesRemaining] = useState<number | null>(null);

  const isProOrAbove = tier === 'pro' || tier === 'institutional' || tier === 'firm' || tier === 'enterprise';

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<AICommentaryResponse>(`/analytics/ai-commentary/${userId}`);
        setCommentary(res.data || null);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to load AI insights';
        setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? msg);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [userId]);

  const submitQuery = async (overrideQuery?: string) => {
    const q = overrideQuery ?? query;
    if (!userId || !q.trim() || queryLoading) return;
    setQueryLoading(true);
    setQueryError(null);
    setQueryAnswer(null);
    if (!overrideQuery) setQuery('');
    try {
      const res = await apiClient.post<AIQueryResponse>(`/analytics/ai-commentary/${userId}/query`, {
        query: q.trim(),
      });
      setQueryAnswer(res.data.answer || res.data.response || 'No answer returned.');
      if (res.data.queriesRemainingToday !== undefined) {
        setQueriesRemaining(res.data.queriesRemainingToday);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to get AI answer';
      setQueryError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? msg);
    } finally {
      setQueryLoading(false);
    }
  };

  if (!userId) {
    return <div className="text-text-muted">Please log in to view AI insights.</div>;
  }

  // Derived tax response highlights
  const showTaxHighlights = queryAnswer && isTaxAnswer(queryAnswer);
  const daysToLt = queryAnswer ? parseDaysToLongTerm(queryAnswer) : null;
  const taxSaving = queryAnswer ? parseTaxSaving(queryAnswer) : null;
  const hasTaxLongTerm = queryAnswer && /long[- ]term/i.test(queryAnswer) && !/short[- ]term/i.test(queryAnswer);
  const hasTaxShortTerm = queryAnswer && /short[- ]term/i.test(queryAnswer);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">AI Insights</h1>
        <p className="text-sm text-text-secondary mt-1">AI-powered portfolio commentary and Q&amp;A.</p>
      </div>

      <AIDataConnectionPanel
        userId={userId}
        featureName="AI Insights"
        valueStatement="analyse your portfolio and answer questions about it"
      />

      {/* Non-dismissible AI disclaimer banner */}
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-1">
        <p className="text-sm text-amber-300 font-medium">
          ⚠️ AI-generated content is not financial advice. Always verify with a qualified advisor.
        </p>
        <p className="text-xs text-amber-300/70">
          When you use AI features, aggregated portfolio data is sent to OpenAI for processing. OpenAI does not use API data for model training. See our <a href="/privacy" className="underline hover:no-underline">Privacy Policy</a>.
        </p>
      </div>

      {/* Free-tier upgrade CTA */}
      {!isProOrAbove && (
        <div className="rounded-xl border border-border bg-surface p-8 text-center space-y-3">
          <p className="text-lg font-semibold text-text-primary">Upgrade to Pro to access AI insights</p>
          <p className="text-sm text-text-secondary">
            Get weekly portfolio narratives, risk commentary, and a natural language Q&amp;A powered by AI.
          </p>
          <p className="text-xs text-text-muted">
            Upgrading unlocks the feature, but the portfolio sources above determine what AI Insights can actually see.
          </p>
          <Link
            to="/billing"
            className="inline-block mt-2 px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium"
          >
            View Plans
          </Link>
        </div>
      )}

      {/* AI Commentary — Pro+ only */}
      {isProOrAbove && (
        <>
          {loading ? (
            <div className="rounded-xl border border-border bg-surface p-6 animate-pulse space-y-3">
              <div className="h-4 bg-surface-secondary rounded w-3/4" />
              <div className="h-4 bg-surface-secondary rounded w-1/2" />
              <div className="h-4 bg-surface-secondary rounded w-2/3" />
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">{error}</div>
          ) : commentary?.upgradeRequired ? (
            <div className="rounded-xl border border-border bg-surface p-6 text-text-muted">
              {commentary.message || 'AI insights require a Pro subscription.'}
            </div>
          ) : !commentary || (!commentary.headline && !commentary.performanceNarrative) ? (
            <div className="rounded-xl border border-border bg-surface p-6 text-text-muted">
              No AI insights available yet. If the portfolio coverage above is still unproven, connect a wallet,
              sync an exchange, or import a CSV first so AI has real data to analyse.
              {process.env.NODE_ENV !== 'production' && (
                <span className="block text-xs mt-1 opacity-60">
                  Configure OPENAI_API_KEY to enable AI commentary.
                </span>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-surface p-6 space-y-4">
              {commentary.cached && (
                <p className="text-xs text-text-muted">Cached analysis · refreshes every 24h</p>
              )}
              {commentary.headline && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-1">Headline</p>
                  <p className="text-text-primary font-medium">{commentary.headline}</p>
                </div>
              )}
              {commentary.performanceNarrative && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-1">Performance</p>
                  <p className="text-text-primary">{commentary.performanceNarrative}</p>
                </div>
              )}
              {!!commentary.topMovers?.length && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-1">Top Movers</p>
                  <ul className="list-disc pl-5 space-y-1 text-text-primary">
                    {commentary.topMovers.map((m, idx) => <li key={idx}>{m}</li>)}
                  </ul>
                </div>
              )}
              {!!commentary.keyRiskFlags?.length && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-1">Risk Flags</p>
                  <ul className="list-disc pl-5 space-y-1 text-text-primary">
                    {commentary.keyRiskFlags.map((r, idx) => <li key={idx}>{r}</li>)}
                  </ul>
                </div>
              )}
              {!!commentary.suggestedActions?.length && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-1">Suggested Actions</p>
                  <ul className="space-y-1">
                    {commentary.suggestedActions.map((s, idx) => (
                      <li key={idx} className="text-text-primary text-sm">
                        {s.action}
                        <span className="ml-2 text-xs text-text-muted italic">({s.label})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {commentary.generatedAt && (
                <p className="text-xs text-text-muted">Generated {new Date(commentary.generatedAt).toLocaleString()}</p>
              )}
            </div>
          )}

          {isProOrAbove && (
            <div className="rounded-xl border border-primary-500/30 bg-primary-500/5 p-5 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-text-primary">Full Portfolio Optimisation</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Get prescriptive BUY / SELL / HOLD recommendations with reasoning grounded in your risk metrics.
                </p>
              </div>
              <Link to="/ai-optimizer" className="btn btn--primary text-sm whitespace-nowrap">
                Full Portfolio Optimisation →
              </Link>
            </div>
          )}

          <div className="rounded-xl border border-border bg-surface p-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-text-primary">Ask AI a follow-up question</p>
              {queriesRemaining !== null && (
                <p className="text-xs text-text-muted">{queriesRemaining} queries remaining today</p>
              )}
            </div>

            {/* Tax quick-reply chip row */}
            <div className="flex flex-wrap gap-2">
              {TAX_QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => void submitQuery(q)}
                  disabled={queryLoading}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void submitQuery()}
                placeholder="e.g. Should I sell my BTC now or wait for long-term treatment?"
                className="flex-1 rounded-lg bg-app border border-border px-3 py-2 text-text-primary"
              />
              <button
                onClick={() => void submitQuery()}
                disabled={!query.trim() || queryLoading}
                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white"
              >
                {queryLoading ? 'Asking…' : 'Ask'}
              </button>
            </div>
            {queryError && <p className="text-sm text-red-300">{queryError}</p>}
            {queryAnswer && (
              <div className="rounded-lg bg-app border border-border p-4 space-y-3">
                <p className="text-xs uppercase tracking-wide text-text-muted">AI Answer</p>

                {/* Tax-specific highlights */}
                {showTaxHighlights && (
                  <div className="flex flex-wrap gap-2">
                    {hasTaxShortTerm && !hasTaxLongTerm && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                        ⏳ Short-Term Treatment
                      </span>
                    )}
                    {hasTaxLongTerm && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                        ✓ Long-Term Treatment
                      </span>
                    )}
                    {daysToLt !== null && daysToLt > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                        📅 Long-Term in {daysToLt}d
                      </span>
                    )}
                    {taxSaving !== null && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
                        💰 Est. saving: <span className="font-mono">${taxSaving.toLocaleString()}</span>
                      </span>
                    )}
                  </div>
                )}

                <p className="text-text-primary whitespace-pre-wrap">{queryAnswer}</p>

                {/* Tax disclaimer footer */}
                {showTaxHighlights && (
                  <p className="text-xs text-amber-300/80 italic border-t border-border pt-2 mt-2">
                    Tax estimates are for informational purposes only. Consult a qualified tax professional.
                  </p>
                )}
                {!showTaxHighlights && (
                  <p className="text-xs text-amber-300/80 italic">
                    AI-generated · informational only · not financial advice
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AIInsightsPage;
